import {
  isOfferedOptionValue,
  pickSingleSpokenValue,
  resolveOfferedOptionValues,
} from './shared-responder-v2-core-option.helpers';
import {
  applySelectionLimit,
  awaitsSelectionConfirmation,
  isMultiSelect,
  limitSpokenSelection,
  resolveRequiredSelectionCount,
} from './shared-responder-v2-core-selection.helpers';
import {
  RESPONSE_MODES,
  type ResponseMode,
} from './shared-responder-v2-core-session.consts';
import {
  createThreadBehaviour,
  type ResponderDraftEvent,
} from './shared-responder-v2-core-thread-behaviour';
import {
  OTHER_OPTION_VALUE,
  THREAD_KINDS,
} from './shared-responder-v2-core-thread.consts';
import type {
  QuestionSpec,
  ResponderAnswer,
} from './shared-responder-v2-core-thread.types';

/** multi-select → an empty selection set; single-select has nothing to seed. */
export function createChoiceEmptyDraft(
  spec: QuestionSpec
): ResponderAnswer | null {
  return isMultiSelect(spec)
    ? { kind: 'multiSelect', optionValues: [], otherText: null }
    : null;
}

function reduceSpokenChoicePick(
  draft: ResponderAnswer | null,
  spokenValues: readonly string[],
  spec: QuestionSpec
): ResponderAnswer | null {
  if (!isMultiSelect(spec)) {
    const optionValue = pickSingleSpokenValue(
      spokenValues.filter((value) => isOfferedOptionValue(value, spec))
    );

    return optionValue === undefined
      ? null
      : reduceChoiceDraft(draft, { type: 'optionToggled', optionValue }, spec);
  }

  const otherText = draft?.kind === 'multiSelect' ? draft.otherText : null;
  const optionValues = limitSpokenSelection(
    resolveOfferedOptionValues(spokenValues, spec),
    spec.maxSelections
  );

  return {
    kind: 'multiSelect',
    optionValues,
    otherText: optionValues.includes(OTHER_OPTION_VALUE) ? otherText : null,
  };
}

/**
 * Single-select replaces the draft outright — there is only ever one chosen
 * value. Multi-select toggles membership, then defers to
 * `applySelectionLimit`, which drops the oldest pick over the cap.
 * `textChanged` writes `otherText` too, but only while Other is the current
 * pick — a dictated utterance on a plain option, or on any other kind, is a
 * no-op, since quant kinds otherwise have no free-text draft to react to.
 *
 * Both branches null out `otherText` the moment Other stops being the (or a)
 * chosen value: the field that holds it unmounts along with the pick, so any
 * text left behind would be neither visible nor editable — and, if Other is
 * picked again later, would resurface without the responder having retyped it.
 */
export function reduceChoiceDraft(
  draft: ResponderAnswer | null,
  event: ResponderDraftEvent,
  spec: QuestionSpec
): ResponderAnswer | null {
  if (event.type === 'optionToggled') {
    if (isMultiSelect(spec)) {
      const currentValues =
        draft?.kind === 'multiSelect' ? draft.optionValues : [];
      const otherText = draft?.kind === 'multiSelect' ? draft.otherText : null;
      const toggledValues = currentValues.includes(event.optionValue)
        ? currentValues.filter((value) => value !== event.optionValue)
        : [...currentValues, event.optionValue];
      const optionValues = applySelectionLimit(
        toggledValues,
        spec.maxSelections,
        otherText
      );

      return {
        kind: 'multiSelect',
        optionValues,
        otherText: optionValues.includes(OTHER_OPTION_VALUE) ? otherText : null,
      };
    }

    const otherText = draft?.kind === 'singleSelect' ? draft.otherText : null;

    return {
      kind: 'singleSelect',
      optionValue: event.optionValue,
      otherText: event.optionValue === OTHER_OPTION_VALUE ? otherText : null,
    };
  }

  if (event.type === 'spokenPick') {
    return reduceSpokenChoicePick(draft, event.optionValues, spec);
  }

  if (
    event.type === 'otherTextChanged' ||
    (event.type === 'textChanged' && isOtherOptionSelected(draft))
  ) {
    if (draft?.kind === 'singleSelect' || draft?.kind === 'multiSelect') {
      return { ...draft, otherText: event.text };
    }

    return draft;
  }

  return draft;
}

/**
 * Selecting the "Other" option value is not, on its own, a complete answer:
 * the responder has declared intent to type, and the pick stays incomplete
 * until `otherText` actually holds something, on both selection modes.
 */
export function isChoiceDraftComplete(
  draft: ResponderAnswer | null,
  spec: QuestionSpec
): boolean {
  if (isOtherTextMissing(draft)) {
    return false;
  }

  if (isMultiSelect(spec)) {
    return (
      draft?.kind === 'multiSelect' &&
      draft.optionValues.length >= resolveRequiredSelectionCount(spec)
    );
  }

  return draft?.kind === 'singleSelect';
}

/**
 * The typed Other text while "Other" is part of the current pick (`''` before
 * anything is typed), or `null` when Other is not picked at all.
 */
export function resolveSelectedOtherText(
  draft: ResponderAnswer | null
): string | null {
  if (
    draft?.kind === 'singleSelect' &&
    draft.optionValue === OTHER_OPTION_VALUE
  ) {
    return draft.otherText ?? '';
  }

  if (
    draft?.kind === 'multiSelect' &&
    draft.optionValues.includes(OTHER_OPTION_VALUE)
  ) {
    return draft.otherText ?? '';
  }

  return null;
}

/**
 * Whether the current pick includes "Other" — the draft holds nothing to
 * submit yet, but the responder has declared intent to type, so this decides
 * both whether an auto-advance may fire and whether Continue must appear.
 */
export function isOtherOptionSelected(draft: ResponderAnswer | null): boolean {
  return resolveSelectedOtherText(draft) !== null;
}

export function isOtherTextMissing(draft: ResponderAnswer | null): boolean {
  return resolveSelectedOtherText(draft)?.trim() === '';
}

/**
 * From the design sync: Continue only applies to "at least" / "up to"
 * selections, which need the responder to confirm the set explicitly —
 * "exactly N" auto-advances the moment N options are picked. Single-select
 * only needs Continue when the question is optional, since picking an option
 * there auto-advances too.
 */
export function isChoiceContinueButtonVisible(
  spec: QuestionSpec,
  mode: ResponseMode
): boolean {
  if (mode !== RESPONSE_MODES.Text) {
    return false;
  }

  return isMultiSelect(spec)
    ? awaitsSelectionConfirmation(spec)
    : !spec.isRequired;
}

export const CHOICE_THREAD_BEHAVIOUR = createThreadBehaviour({
  kind: THREAD_KINDS.Choice,
  advancesOnSurfacePick: true,
  spokenAffordances: { extractsSpokenPick: true },
  emptyDraft: createChoiceEmptyDraft,
  reduceDraft: reduceChoiceDraft,
  isDraftComplete: isChoiceDraftComplete,
  showsContinueButton: isChoiceContinueButtonVisible,
});
